import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { NotificationManager } from 'react-notifications';
import { Icon } from "@iconify/react/dist/iconify.js";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios"

const baseUrl = process.env.REACT_APP_API;

const SignInLayer = () => {
  const imagePath = `${process.env.PUBLIC_URL}/assets/images/BlueAsterAR.png`;
  const [username, setusername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({ username: "", password: "" });
  const [loading, setLoading] = useState(true);
  const [loginloading, setLoginLoading] = useState(false);
  const navigate = useNavigate();

  const validate = () => {
    const next = { username: "", password: "" };
    const trimmedUser = username.trim();

    if (!trimmedUser) {
      next.username = "Username is required.";
    } else if (trimmedUser.length < 3) {
      next.username = "Username must be at least 3 characters.";
    } else if (!/^[A-Za-z0-9._@-]+$/.test(trimmedUser)) {
      next.username = "Use only letters, numbers, and . _ @ - characters.";
    }

    if (!password) {
      next.password = "Password is required.";
    } else if (password.length < 6) {
      next.password = "Password must be at least 6 characters.";
    }

    setErrors(next);
    return !next.username && !next.password;
  };

  const handleUsernameChange = (e) => {
    setusername(e.target.value);
    if (errors.username) setErrors((prev) => ({ ...prev, username: "" }));
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    if (errors.password) setErrors((prev) => ({ ...prev, password: "" }));
  };

  const handleLogin = async (event) => {
    event.preventDefault();
    if (!validate()) return;

    setLoginLoading(true);
    const email = username.trim();

    try {
      const ipResponse = await axios.get("https://api.ipify.org?format=json");
      const userIp = ipResponse.data.ip;

      const response = await fetch(`${baseUrl}/api/auth/admin-login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password, ip: userIp }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message);
      }
      NotificationManager.success('Logged in successful!');
      sessionStorage.setItem("AdminAuthToken", data.token);
      navigate("/clients");
    } catch (error) {
      NotificationManager.error(error.message || "Login failed. Please try again.");
    }
    finally {
      setLoginLoading(false);
    }
  };

  useEffect(() => {
    const timeout = setTimeout(() => setLoading(false), 2000);
    return () => clearTimeout(timeout);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("AdminAuthToken");
    if (token) {
      navigate("/clients");
    } else {
      setLoading(false);
    }
  }, []);

  return (
    <>
      {loading ? (
        <div className="ba-loader-wrap">
          <div className="ba-loader-inner">
            <img src={imagePath} alt="Logo" className="ba-loader-logo" />
            <div className="ba-spinner"></div>
          </div>
        </div>
      ) : (
        <section className="ba-auth">
          {/* Decorative background blobs */}
          <span className="ba-blob ba-blob-1" />
          <span className="ba-blob ba-blob-2" />
          <span className="ba-blob ba-blob-3" />

          <div className="ba-auth-card">
            {/* Left brand panel */}
            <aside className="ba-brand-panel">
              <div className="ba-brand-top">
                <div className="ba-brand-logo">
                  <img src={imagePath} alt="BlueAster Capital" />
                </div>
              </div>

              <div className="ba-brand-content">
                <h2 className="ba-brand-title">
                  Fund Tracking <br /> <span>Admin Console</span>
                </h2>
                <p className="ba-brand-sub">
                  Securely manage clients, portfolios and fund performance — all in one place.
                </p>

                <ul className="ba-feature-list">
                  <li>
                    <span className="ba-feature-ico">
                      <Icon icon="solar:chart-2-bold-duotone" />
                    </span>
                    <div>
                      <strong>Real-time Portfolio Insights</strong>
                      <small>Track Value, returns and allocations live.</small>
                    </div>
                  </li>
                  <li>
                    <span className="ba-feature-ico">
                      <Icon icon="solar:shield-check-bold-duotone" />
                    </span>
                    <div>
                      <strong>Bank-grade Security</strong>
                      <small>Encrypted access with IP-based auditing.</small>
                    </div>
                  </li>
                  <li>
                    <span className="ba-feature-ico">
                      <Icon icon="solar:users-group-rounded-bold-duotone" />
                    </span>
                    <div>
                      <strong>Unified Client View</strong>
                      <small>Subscribers, transactions and vesting in one dashboard.</small>
                    </div>
                  </li>
                </ul>
              </div>

              <div className="ba-brand-foot">
                <span>© {new Date().getFullYear()} BlueAster Capital</span>
              </div>
            </aside>

            {/* Right form panel */}
            <div className="ba-form-panel">
              <div className="ba-form-inner">
                <div className="ba-mobile-logo">
                  <img src={imagePath} alt="Logo" />
                </div>

                <div className="ba-form-head">
                  <span className="ba-pill">
                    <Icon icon="solar:verified-check-bold" /> Admin Portal
                  </span>
                  <h3>Welcome back</h3>
                  <p>Sign in to your Fund Tracking Admin account to continue.</p>
                </div>

                <form onSubmit={handleLogin} className="ba-form" autoComplete="on" noValidate>
                  <label className="ba-field">
                    <span className="ba-field-label">Username</span>
                    <span className={`ba-input-wrap ${errors.username ? 'has-error' : ''}`}>
                      <span className="ba-input-ico">
                        <Icon icon="mage:email" />
                      </span>
                      <input
                        type="text"
                        placeholder="Enter your username"
                        value={username}
                        onChange={handleUsernameChange}
                        aria-invalid={!!errors.username}
                        aria-describedby="ba-err-username"
                      />
                    </span>
                    {errors.username && (
                      <span id="ba-err-username" className="ba-error-msg">
                        <Icon icon="solar:danger-triangle-bold" /> {errors.username}
                      </span>
                    )}
                  </label>

                  <label className="ba-field">
                    <span className="ba-field-label">Password</span>
                    <span className={`ba-input-wrap ${errors.password ? 'has-error' : ''}`}>
                      <span className="ba-input-ico">
                        <Icon icon="solar:lock-password-outline" />
                      </span>
                      <input
                        type={showPassword ? "text" : "password"}
                        placeholder="Enter your password"
                        value={password}
                        onChange={handlePasswordChange}
                        aria-invalid={!!errors.password}
                        aria-describedby="ba-err-password"
                      />
                      <button
                        type="button"
                        className="ba-eye"
                        onClick={() => setShowPassword((s) => !s)}
                        aria-label={showPassword ? "Hide password" : "Show password"}
                      >
                        <Icon icon={showPassword ? "solar:eye-bold" : "solar:eye-closed-bold"} />
                      </button>
                    </span>
                    {errors.password && (
                      <span id="ba-err-password" className="ba-error-msg">
                        <Icon icon="solar:danger-triangle-bold" /> {errors.password}
                      </span>
                    )}
                  </label>

                  <button
                    type="submit"
                    className={`ba-submit ${loginloading ? 'is-loading' : ''}`}
                    disabled={loginloading}
                  >
                    {loginloading ? (
                      <>
                        <span className="ba-btn-spin" /> Signing in...
                      </>
                    ) : (
                      <>
                        Sign In <Icon icon="solar:arrow-right-linear" />
                      </>
                    )}
                  </button>
                </form>

                <div className="ba-form-foot">
                  <Icon icon="solar:lock-keyhole-minimalistic-bold-duotone" />
                  <span>Your connection is encrypted and monitored.</span>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      <style>{`
        @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
        @keyframes ba-float {
          0%, 100% { transform: translateY(0) translateX(0); }
          50% { transform: translateY(-20px) translateX(10px); }
        }
        @keyframes ba-fadeUp {
          from { opacity: 0; transform: translateY(14px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .ba-loader-wrap {
          display:flex; justify-content:center; align-items:center;
          min-height:100vh;
          background: linear-gradient(135deg, #eef2ff 0%, #f8fafc 50%, #eff6ff 100%);
        }
        .ba-loader-inner { text-align:center; }
        .ba-loader-logo { max-width: 200px; }
        .ba-spinner {
          border: 4px solid rgba(13,110,253,0.15);
          border-left-color: #0d6efd;
          width: 40px; height: 40px;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 20px auto 0;
        }

        .ba-auth {
          position: relative;
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 24px;
          background:
            radial-gradient(1200px 600px at 10% 0%, rgba(59,130,246,0.18), transparent 60%),
            radial-gradient(900px 500px at 100% 100%, rgba(99,102,241,0.18), transparent 60%),
            linear-gradient(135deg, #0b1220 0%, #111a30 50%, #0b1220 100%);
          overflow: hidden;
        }
        .ba-blob {
          position: absolute; border-radius: 50%; filter: blur(60px); opacity: .55;
          animation: ba-float 14s ease-in-out infinite;
          pointer-events: none;
        }
        .ba-blob-1 { width: 320px; height: 320px; top: -80px; left: -80px;
          background: radial-gradient(circle, #3b82f6, transparent 60%); }
        .ba-blob-2 { width: 380px; height: 380px; bottom: -120px; right: -100px;
          background: radial-gradient(circle, #6366f1, transparent 60%);
          animation-delay: 3s; }
        .ba-blob-3 { width: 260px; height: 260px; top: 40%; left: 50%;
          background: radial-gradient(circle, #22d3ee, transparent 60%);
          animation-delay: 6s; opacity: .35; }

        .ba-auth-card {
          position: relative;
          width: 100%;
          max-width: 1080px;
          display: grid;
          grid-template-columns: 1.05fr 1fr;
          background: rgba(255,255,255,0.96);
          border-radius: 24px;
          overflow: hidden;
          box-shadow:
            0 30px 80px rgba(2,6,23,0.45),
            0 4px 12px rgba(2,6,23,0.25);
          animation: ba-fadeUp .5s ease both;
          backdrop-filter: blur(8px);
        }

        /* ===== Brand panel ===== */
        .ba-brand-panel {
          position: relative;
          padding: 40px 36px;
          color: #fff;
          background:
            radial-gradient(600px 300px at 0% 0%, rgba(255,255,255,0.18), transparent 60%),
            radial-gradient(500px 300px at 100% 100%, rgba(34,211,238,0.25), transparent 60%),
            linear-gradient(135deg, #0d3b8e 0%, #1d4ed8 45%, #0ea5e9 100%);
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          min-height: 620px;
        }
        .ba-brand-panel::before {
          content: "";
          position: absolute; inset: 0;
          background-image:
            radial-gradient(rgba(255,255,255,0.18) 1px, transparent 1px);
          background-size: 22px 22px;
          opacity: .25;
          pointer-events: none;
        }
        .ba-brand-top { position: relative; z-index: 1; }
        .ba-brand-logo {
          background: rgba(255,255,255,0.95);
          display: inline-flex;
          padding: 10px 16px;
          border-radius: 14px;
          box-shadow: 0 10px 24px rgba(0,0,0,0.15);
        }
        .ba-brand-logo img { height: 44px; display:block; }

        .ba-brand-content { position: relative; z-index: 1; margin-top: 28px; }
        .ba-brand-title {
          font-size: 34px;
          line-height: 1.15;
          font-weight: 700;
          margin: 0 0 12px;
          letter-spacing: -0.5px;
        }
        .ba-brand-title span {
          background: linear-gradient(90deg, #ffffff, #a5f3fc);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .ba-brand-sub {
          font-size: 15px;
          line-height: 1.6;
          color: rgba(255,255,255,0.85);
          margin: 0 0 28px;
          max-width: 420px;
        }

        .ba-feature-list { list-style:none; padding:0; margin:0; display:flex; flex-direction:column; gap:14px; }
        .ba-feature-list li {
          display:flex; align-items:flex-start; gap:14px;
          padding: 14px 14px;
          background: rgba(255,255,255,0.08);
          border: 1px solid rgba(255,255,255,0.14);
          border-radius: 14px;
          backdrop-filter: blur(4px);
        }
        .ba-feature-ico {
          flex-shrink:0;
          width: 40px; height: 40px;
          display:inline-flex; align-items:center; justify-content:center;
          background: rgba(255,255,255,0.18);
          border-radius: 10px;
          font-size: 22px;
        }
        .ba-feature-list strong { display:block; font-size:14px; font-weight:600; }
        .ba-feature-list small { display:block; font-size:12.5px; color: rgba(255,255,255,0.78); margin-top:2px; }

        .ba-brand-foot {
          position: relative; z-index: 1;
          margin-top: 28px;
          font-size: 12px;
          color: rgba(255,255,255,0.7);
        }

        /* ===== Form panel ===== */
        .ba-form-panel {
          padding: 48px 44px;
          display: flex;
          align-items: center;
          background: #ffffff;
        }
        .ba-form-inner { width: 100%; max-width: 420px; margin: 0 auto; }

        .ba-mobile-logo { display:none; text-align:center; margin-bottom: 18px; }
        .ba-mobile-logo img { max-width: 160px; }

        .ba-form-head { margin-bottom: 24px; }
        .ba-pill {
          display:inline-flex; align-items:center; gap:6px;
          font-size: 12px; font-weight: 600;
          color: #1d4ed8;
          background: #eff6ff;
          padding: 6px 12px;
          border-radius: 999px;
          margin-bottom: 14px;
        }
        .ba-form-head h3 {
          font-size: 28px;
          font-weight: 700;
          color: #0f172a;
          margin: 0 0 6px;
          letter-spacing: -0.3px;
        }
        .ba-form-head p {
          color: #64748b;
          font-size: 14px;
          margin: 0;
          line-height: 1.5;
        }

        .ba-form { display:flex; flex-direction:column; gap: 16px; }
        .ba-field { display:flex; flex-direction:column; gap:6px; }
        .ba-field-label {
          font-size: 13px;
          font-weight: 600;
          color: #334155;
        }
        .ba-input-wrap {
          position: relative;
          display:flex; align-items:center;
          background: #f8fafc;
          border: 1.5px solid #e2e8f0;
          border-radius: 12px;
          transition: all .2s ease;
        }
        .ba-input-wrap:focus-within {
          border-color: #2563eb;
          background: #fff;
          box-shadow: 0 0 0 4px rgba(37,99,235,0.12);
        }
        .ba-input-wrap.has-error {
          border-color: #ef4444;
          background: #fef2f2;
        }
        .ba-input-wrap.has-error:focus-within {
          box-shadow: 0 0 0 4px rgba(239,68,68,0.15);
        }
        .ba-error-msg {
          display:inline-flex; align-items:center; gap:6px;
          margin-top: 4px;
          font-size: 12.5px;
          color: #dc2626;
          font-weight: 500;
        }
        .ba-error-msg svg { font-size: 14px; }
        .ba-input-ico {
          display:inline-flex; align-items:center; justify-content:center;
          padding: 0 12px;
          color: #64748b;
          font-size: 20px;
        }
        .ba-input-wrap input {
          flex:1;
          height: 50px;
          background: transparent;
          border: none;
          outline: none;
          font-size: 14.5px;
          color: #0f172a;
          padding-right: 14px;
        }
        .ba-input-wrap input::placeholder { color: #94a3b8; }
        .ba-eye {
          background: transparent; border: none;
          padding: 0 14px;
          cursor: pointer;
          color: #64748b;
          font-size: 18px;
          display:inline-flex; align-items:center;
        }
        .ba-eye:hover { color: #2563eb; }

        .ba-submit {
          margin-top: 8px;
          height: 52px;
          border: none;
          border-radius: 12px;
          color: #fff;
          font-weight: 600;
          font-size: 15px;
          letter-spacing: .2px;
          cursor: pointer;
          display:inline-flex; align-items:center; justify-content:center; gap: 8px;
          background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 50%, #0ea5e9 100%);
          background-size: 200% 200%;
          background-position: 0% 50%;
          box-shadow: 0 10px 24px rgba(37,99,235,0.35);
          transition: all .25s ease;
        }
        .ba-submit:hover:not(:disabled) {
          background-position: 100% 50%;
          transform: translateY(-1px);
          box-shadow: 0 14px 28px rgba(37,99,235,0.45);
        }
        .ba-submit:disabled { opacity: .8; cursor: not-allowed; }
        .ba-btn-spin {
          width: 18px; height: 18px;
          border: 2px solid rgba(255,255,255,0.4);
          border-left-color: #fff;
          border-radius: 50%;
          animation: spin .8s linear infinite;
        }

        .ba-form-foot {
          margin-top: 22px;
          display:flex; align-items:center; gap:8px;
          font-size: 12.5px;
          color: #64748b;
          justify-content: center;
        }
        .ba-form-foot svg { color: #16a34a; font-size: 16px; }

        /* ===== Responsive ===== */
        @media (max-width: 960px) {
          .ba-auth-card { grid-template-columns: 1fr; max-width: 520px; }
          .ba-brand-panel { display: none; }
          .ba-mobile-logo { display: block; }
          .ba-form-panel { padding: 36px 28px; }
        }
        @media (max-width: 480px) {
          .ba-auth { padding: 14px; }
          .ba-form-panel { padding: 28px 20px; }
          .ba-form-head h3 { font-size: 24px; }
          .ba-input-wrap input { height: 48px; font-size: 14px; }
          .ba-submit { height: 48px; font-size: 14px; }
          .ba-mobile-logo img { max-width: 140px; }
        }
        @media (min-width: 961px) and (max-width: 1100px) {
          .ba-brand-panel { padding: 32px 28px; }
          .ba-brand-title { font-size: 28px; }
          .ba-form-panel { padding: 40px 32px; }
        }
      `}</style>
    </>
  );
};

export default SignInLayer;
